<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip placement="bottom" content="再読込">
        <v-btn icon @click="$emit('reload')">
          <v-icon>mdi-reload</v-icon>
        </v-btn>
      </el-tooltip>
      <v-spacer/>
      <toolbar-inline>
        <trade-contract-filter
            @apply-filter="tradeContractFilter = $event"
            :trade-contracts="tradeContractsView">
        </trade-contract-filter>
      </toolbar-inline>
      <toolbar-inline>
        <v-text-field
            v-model="filterText"
            clearable
            placeholder="検索">
        </v-text-field>
      </toolbar-inline>
    </template>

    <el-table
        :data="tradeContractsViewFiltered"
        :row-class-name="tableRowClassName"
        :default-sort="{prop: 'createdAt', order: 'descending'}"
        :height="tabItemBounds.height"
        border>
      <el-table-column
          width="250"
          prop="name"
          label="取引契約名"
          fixed="left"
          :sort-method="buildSort(row => row.name)"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.name"
                :value-id="row.id">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          width="80"
          align="center"
          prop="amiIssuer"
          label="方向"
          sortable>
        <template #default="{row}">
          <template v-if="row.amiIssuer">
            <span>発行</span>
            <el-tooltip placement="right">
              <template #content>
                {{ account.owner.name }}が発行しました
              </template>
              <template #default>
                <v-icon small color="blue" class="ml-1">mdi-information</v-icon>
              </template>
            </el-tooltip>
          </template>
          <template v-if="row.amiRecipient">
            <span>受領</span>
            <el-tooltip placement="right">
              <template #content>
                {{ account.owner.name }}が受領しました
              </template>
              <template #default>
                <v-icon small color="blue" class="ml-1">mdi-information</v-icon>
              </template>
            </el-tooltip>
          </template>
        </template>
      </el-table-column>

      <el-table-column
          width="100"
          align="center"
          prop="statusText"
          label="状態"
          sortable>
      </el-table-column>

      <el-table-column
          min-width="150"
          prop="companionOwnerName"
          label="取引先事業者"
          sortable>
        <template #default="{row}">
          <table-inline v-if="!row.isPublishMode && row.companionTerminalName">
            <text-field-like
                style="font-size: 85%;"
                :value="row.companionTerminalName">
            </text-field-like>
          </table-inline>
          <table-inline>
            <text-field-like
                :value="row.companionOwnerName"
                :placeholder="row.isPublishMode ? '募集中' : '相手側で未承認'">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          min-width="200"
          prop="tradeContractSource.dataComponentModelName"
          label="データ構成モデル名"
          :sort-method="buildSort(row => row.tradeContractSource.dataComponentModelName)"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.tradeContractSource.dataComponentModelName"
                :message="row.tradeContractSource.dataComponentModelDictionaryName"
                placeholder="未選択">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          min-width="130"
          prop="tradeContractSource.dataImplementation.controller.name"
          label="コントローラ"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.tradeContractSource.dataImplementation
                ? row.tradeContractSource.dataImplementation.controller.name
                : null"
                placeholder="未選択">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          width="80"
          align="center"
          prop="serviceTypeText"
          label="区分"
          sortable>
        <template #default="{row}">
          {{ row.serviceTypeText }}
          <el-tooltip placement="right">
            <template #content>
              {{ account.owner.name }}が{{ row.serviceTypeText }}しています
            </template>
            <template #default>
              <v-icon small color="blue" class="ml-1">mdi-information</v-icon>
            </template>
          </el-tooltip>
        </template>
      </el-table-column>

      <el-table-column
          width="100"
          prop="createdAt"
          label="作成日"
          sortable>
        <template #default="{row}">{{ formatDate(row.createdAt) }}</template>
      </el-table-column>

      <el-table-column
          width="110"
          prop="expireDate"
          label="回答期限"
          sortable>
        <template #default="{row}">{{ formatDate(row.expireDate) }}</template>
      </el-table-column>

      <el-table-column
          width="93"
          align="right"
          fixed="right"
          column-key="editing"
          v-if="isEditable">
        <template #default="{row}">
          <icon-btns>
            <!-- 自分が受領者の場合 -->
            <template v-if="row.amiRecipient && !row.amiIssuer && isEditable">
              <!-- 自分が受領者かつターゲット側の場合のみ編集可能 -->
              <icon-btn
                  v-if="row.amiTarget"
                  type="edit"
                  :to="editLocation(row)">
              </icon-btn>
              <!-- そうでない場合は閲覧 -->
              <icon-btn
                  v-else
                  type="view"
                  :to="{name: '/viewContract', params: {tradeContractId: row.id}}">
              </icon-btn>
            </template>

            <!-- 自分が発行者の場合 -->
            <template v-if="row.amiIssuer">
              <!-- 準備中は削除可能 -->
              <icon-btn
                  v-if="['preparing', 'rejected'].includes(row.status)"
                  type="delete"
                  @click="openDeleteDialog(row)">
              </icon-btn>

              <!-- 準備中・修正中は編集可能 -->
              <icon-btn
                  v-if="['preparing','revising'].includes(row.status)"
                  type="edit"
                  :to="editLocation(row)">
              </icon-btn>

              <!-- 準備中・修正中以外のステータスの場合は閲覧のみ -->
              <icon-btn
                  v-else
                  type="view"
                  :to="{name: '/viewContract', params: {tradeContractId: row.id}}">
              </icon-btn>
            </template>
          </icon-btns>
        </template>
      </el-table-column>
    </el-table>

    <simple-dialog
        :value="deleteDialog"
        title-text="取引契約削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下の取引契約を削除します。本当によろしいですか？"
        @close="closeDeleteDialog">
      <v-row>
        <v-col>
          <text-field-like
              :value="deleteDialog.name"
              label="契約名">
          </text-field-like>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <text-field-like
              :value="deleteDialog.description"
              label="説明"
              placeholder="未設定"
              multi-line>
          </text-field-like>
        </v-col>
      </v-row>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import Formatter from "@/utils/Formatter";
import {mapGetters, mapMutations} from "vuex";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TradeContractFilter from "@/components/organisms/TradeContractFilter.vue";
import natsort from "natsort";
import TableUtil from "@/components/pages/contract/TradeContractList/TableUtil";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";
import TradeContractUtils from "@/components/pages/contract/TradeContract/TradeContractUtils";

const sortFn = natsort();

export default {
  name: 'PretradingTab',
  mixins: [
    PathInfoMixin,
    Formatter,
    TableUtil,
    TradeContractUtils,
  ],
  components: {
    TradeContractFilter,
    IconBtn,
    IconBtns,
    TableInline,
    TextFieldLike,
    TabItemTemplate,
    SimpleDialog,
    ToolbarInline,
  },
  inject: ['tabItemBounds'],
  props: {
    tradeContracts: {
      type: Array,
      required: true
    },
  },
  data() {
    return {
      deleteDialog: false,
      tradeContractFilter: () => true,
      filterText: '',
    }
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
    }),
    tradeContractsView() {
      const includeStatus = ['preparing', 'requesting', 'revising', 'applying', 'rejected'];
      return this.tradeContracts
          .filter(tc => includeStatus.includes(tc.status))
          .filter(tc => tc.status !== 'rejected' || tc.amiIssuer); // 不成約の取引契約の場合は発行者のみ表示する
    },
    tradeContractsViewFiltered() {
      const textFilter = tc => {
        const f = this.filterText;
        if(f) {
          return tc.id.includes(f) ||
              tc.statusText?.includes(f) ||
              tc.name.includes(f) ||
              tc.tradeContractSource.dataComponentModelName?.includes(f) ||
              tc.companionOwnerName?.includes(f) ||
              this.formatDate(tc.createdAt).includes(f);
        } else {
          return true;
        }
      };
      return this.tradeContractsView.filter(tc => this.tradeContractFilter(tc) && textFilter(tc));
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    tableRowClassName({ row }) {
      if(row.status === 'preparing') {
        return 'preparing-row';
      }
      if(row.status === 'rejected') {
        return 'rejected-row'
      }
      if (row.amiTarget) {
        return 'target-row';
      }
    },
    openDeleteDialog(row) {
      this.deleteDialog = row;
    },
    async closeDeleteDialog(apply) {
      if(apply) {
        this.$emit('delete', this.deleteDialog.id);
      }
      this.deleteDialog = false;
    },
  }
}
</script>

<style lang="scss" scoped>
@import "src/theme";

::v-deep {
  .el-table tr {
    &.preparing-row {
      @include tr-background-color(#d2e4fb, false);
    }
    &.rejected-row {
      @include tr-background-color(#ffdcdc, false);
    }
    &.target-row {
      @include tr-background-color($tableRowHighlightColor, false);
    }
  }
}
</style>
